import { useAuthStore } from '@/store/authStore'
import { Organization } from '@/types'
import { ChevronDown, Building2, Plus } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export default function OrgSwitcher() {
    const { organizations, currentOrg, setCurrentOrg } = useAuthStore()
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleOrgSelect = (org: Organization) => {
        setCurrentOrg(org)
        localStorage.setItem('spacerub_current_org_id', org.id)
        setIsOpen(false)
        navigate('/dashboard')
    }

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between space-x-2 px-3 py-2 rounded-xl bg-white hover:bg-gray-50 transition-all text-gray-900 text-[9px] font-black border-2 border-slate-200 shadow-sm uppercase tracking-widest"
            >
                <div className="flex items-center space-x-2">
                    <Building2 className="w-3.5 h-3.5 text-primary" />
                    <span className="max-w-[120px] truncate">
                        {currentOrg?.name || 'Selecione'}
                    </span>
                </div>
                <ChevronDown className={`w-2.5 h-2.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute left-0 mt-3 w-72 rounded-[2rem] shadow-2xl bg-white border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="p-2">
                        <div className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Suas Organizações
                        </div>

                        <div className="max-h-60 overflow-y-auto px-2 space-y-1">
                            {organizations.length === 0 && (
                                <p className="px-4 py-6 text-sm text-gray-400 text-center italic">Nenhuma organização encontrada.</p>
                            )}
                            {organizations.map((org) => (
                                <button
                                    key={org.id}
                                    onClick={() => handleOrgSelect(org)}
                                    className={`w-full text-left px-4 py-4 rounded-2xl text-sm flex items-center justify-between hover:bg-gray-50 transition-all ${currentOrg?.id === org.id ? 'bg-primary-50/50 text-primary-700 shadow-sm' : 'text-gray-600'
                                        }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-gray-600 overflow-hidden shadow-sm">
                                            {org.branding?.logoUrl ? (
                                                <img src={org.branding.logoUrl} alt={org.name} className="w-full h-full object-contain" />
                                            ) : (
                                                <Building2 className="w-5 h-5 text-primary-600" />
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-bold truncate max-w-[140px]">{org.name}</span>
                                            <span className="text-[9px] text-gray-400 font-black uppercase tracking-tighter">{org.plan}</span>
                                        </div>
                                    </div>
                                    {currentOrg?.id === org.id && (
                                        <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="mt-2 pt-2 border-t border-gray-50 space-y-1">
                            <button
                                onClick={() => {
                                    setIsOpen(false)
                                    navigate('/create-org')
                                }}
                                className="w-full text-left px-6 py-4 text-[10px] font-black text-primary-600 hover:bg-primary-50 transition-all flex items-center space-x-3 uppercase tracking-widest"
                            >
                                <Plus className="w-4 h-4" />
                                <span>Criar Nova Organização</span>
                            </button>
                            <button
                                onClick={() => {
                                    setIsOpen(false)
                                    navigate('/join-org')
                                }}
                                className="w-full text-left px-6 py-4 text-[10px] font-black text-gray-500 hover:bg-gray-50 transition-all flex items-center space-x-3 uppercase tracking-widest"
                            >
                                <Building2 className="w-4 h-4" />
                                <span>Entrar com Código</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
